import React from 'react'

const variableCards = [
  {
    key: 'W',
    title: 'Winning Result',
    body: 'The one clear result that makes today a win. Specific, measurable, finishable today, and connected to the month.',
    tone: 'azure'
  },
  {
    key: 'C',
    title: 'Concentration',
    body: 'How deeply and consistently you focused on the Winning Result. One result. One block. One mind.',
    tone: 'sage'
  },
  {
    key: 'T',
    title: 'Time Protected',
    body: 'How well you protected time from distraction, interruption, and context switching. Protected time is what produces output.',
    tone: 'coral'
  }
]

const weakVsStrong = [
  ['Work on sales', 'Send 10 personalized outreach messages to UK travel business owners before 12 PM'],
  ['Improve website', 'Publish the getmyforce AI Agents landing page with one CTA and working lead form'],
  ['Study better', 'Complete 40 math problems from Chapter 3 and review all mistakes'],
  ['Exercise', 'Walk 45 minutes before 8:30 AM'],
  ['Think about strategy', 'Write a one-page GTM plan for the travel AI agent offer']
]

const monthlyAreas = [
  ['Business', 'Generate 50 qualified leads for getmyforce AI Agents'],
  ['Health', 'Reduce weight by 2 kg through walking and food discipline'],
  ['Wealth', 'Complete analysis of 10 high-quality companies'],
  ['Learning', 'Finish one technical course and build one working project'],
  ['Sales', 'Book 15 discovery calls with target customers']
]

const lowHighC = [
  ['Checking WhatsApp while working', 'Phone on DND'],
  ['Switching between five tabs', 'Only required tools open'],
  ['Half-working, half-thinking', 'Fully mentally present'],
  ['Starting and stopping repeatedly', 'Working in protected deep-work blocks'],
  ['Reacting to interruptions', 'Controlling the environment before work starts']
]

const lowHighT = [
  ['“I’ll work whenever I get time”', '“I will work 9:00–10:30 AM”'],
  ['Phone beside laptop', 'Phone outside the room'],
  ['Open calendar, WhatsApp, inbox', 'Only work tools open'],
  ['Available to everyone', 'Deep-work block protected'],
  ['Random interruptions', 'Clear boundaries']
]

const scoreExamples = [
  { label: 'Average Day', values: 'W 5 · C 5 · T 5', result: 'R = 30', note: 'Some clarity, some focus, some distraction.' },
  { label: 'Strong Day', values: 'W 8 · C 8 · T 8', result: 'R = 72', note: 'Clear result, strong concentration, protected time.' },
  { label: 'Busy but Weak Day', values: 'W 3 · C 8 · T 7', result: 'R = 31', note: 'Even with good focus and protected time, the result stays weak because W was unclear.' },
  { label: 'Clear Goal, Poor Focus', values: 'W 9 · C 3 · T 3', result: 'R = 30', note: 'The goal was strong, but focus collapsed. The day becomes average.' }
]

const morningChecklist = [
  'I have written my W as a completed result, not a task',
  'My phone is on DND or away from me',
  'All unnecessary browser tabs are closed',
  'My team or family knows I am in a focus block',
  'I am committed to zero task-switching during this block'
]

const focusBlockSteps = [
  'Pick one W',
  'Set your timer',
  'Remove distractions',
  'Work until the block ends',
  'Do not switch tasks',
  'Record completion honestly'
]

const roleExamples = [
  {
    role: 'Founder',
    monthly: 'Generate 50 qualified leads for getmyforce AI Agents.',
    daily: 'Send 20 personalized messages to travel business owners and book 2 discovery calls.',
    block: '90 minutes outreach, no inbox, no WhatsApp.',
    reflection: 'W was clear. C was 8. T was 6 because calls interrupted the block.'
  },
  {
    role: 'Student',
    monthly: 'Complete full revision of Physics Electricity chapter.',
    daily: 'Solve 40 Current Electricity problems and review every wrong answer.',
    block: '90 minutes problem-solving without phone.',
    reflection: 'W was clear, but concentration dropped after 45 minutes. Tomorrow use 45-minute blocks.'
  },
  {
    role: 'Salesperson',
    monthly: 'Build ₹25 lakh qualified pipeline.',
    daily: 'Call 15 warm leads and schedule 3 demos.',
    block: '60 minutes calls, 30 minutes follow-up notes.',
    reflection: 'T was weak because I kept reacting to internal chats.'
  },
  {
    role: 'Health',
    monthly: 'Build consistent walking and food discipline.',
    daily: 'Complete 45-minute walk before breakfast and eat no fried food today.',
    block: 'Morning health block from 7:00–8:00 AM.',
    reflection: 'W completed. C was strong. T was high because walk happened before work started.'
  }
]

const quickStart = [
  ['Step 1: Define Monthly Result Area', 'Choose the result this month must produce.'],
  ['Step 2: Set Your W', 'Define today’s Winning Result.'],
  ['Step 3: Protect Your Focus Block', 'Set concentration and time protection.'],
  ['Step 4: Execute', 'Work deeply on the W.'],
  ['Step 5: Review R Score', 'Score the day and diagnose what failed.']
]

function FundamentalsPanel() {
  return (
    <div className="panel active">
      <div className="pg-ey">The Unstoppable System · Fundamentals</div>
      <div className="pg-ti">How <em>ResultOS</em> Works</div>
      <div className="pg-su">
        ResultOS is The Unstoppable System — built for people who ship the right result every single day. It starts with the result, protects deep work, and reviews performance honestly.
      </div>

      {/* ── Origin of ResultOS ── */}
      <div className="sec">Origin of ResultOS</div>

      <div className="fund-prose mb24">
        <p className="fund-prose-intro">Here's the intellectual lineage of ResultOS — how each thinker's core idea maps to the system:</p>

        <h3 className="fund-prose-h">Napoleon Hill — Burning desire</h3>
        <p>Hill spent 20 years interviewing 500 of the most successful people of his era — industrialists, inventors, politicians, writers — looking for the single factor that separated those who achieved from those who didn't. His finding, published in <em>Think and Grow Rich</em>, was not what most people expect. It wasn't discipline. It wasn't talent. It wasn't even hard work. It was desire. Specifically, a burning, obsessive desire for a definite outcome — the kind that makes a person show up on day 47 of something, when the novelty is completely gone and the discomfort is fully present.</p>
        <p>Hill distinguished desire sharply from a wish or a preference. A wish is passive. A preference is conditional. Desire, in Hill's framework, is the thing that reorganises your behaviour even when you don't feel like it. It's not something you manufacture with willpower — it's something you discover about what you genuinely want badly enough to endure honest execution every single day.</p>
        <p>In ResultOS, burning desire sits above the equation. It is not a variable — it's the reason the equation runs at all. Your W, your focus blocks, your honest end-of-day scoring — none of it sustains without the fire underneath. That's why it lives at the top of your Monthly Results Area, always visible, always first. Before you define what you're doing today, you need to remember why you care enough to do it honestly.</p>

        <h3 className="fund-prose-h">Brian Tracy — The W variable</h3>
        <p>Tracy spent decades studying why some people achieve dramatically more than others with the same 24 hours. His conclusion was deceptively simple: the highest performers identify their single most important task and complete it before doing anything else. He called it "eating the frog" — the idea that if the first thing you do each morning is the hardest, most impactful task, everything else feels easier by comparison.</p>
        <p>What most people miss about Tracy's work is that it's not about willpower. It's about <em>sequence</em>. The decision of what comes first is the decision that shapes the entire day.</p>
        <p>In ResultOS, this became the W — one winning result, defined with enough clarity that you could hand it to someone else and they'd know exactly what done looks like. The morning ritual doesn't ask "what are your tasks today?" It asks "what is the ONE result that makes today a win?" That's pure Tracy.</p>

        <h3 className="fund-prose-h">Elon Musk — The equation itself</h3>
        <p>Musk's contribution to ResultOS isn't about hustle or working 100-hour weeks. It's about first principles decomposition — the practice of breaking a complex problem down to its irreducible components rather than reasoning by analogy. Most productivity systems are built by analogy: "successful people wake up at 5am, journal, meditate, exercise, then work." That's copying the surface pattern without understanding the physics underneath.</p>
        <p>First principles asks: what actually produces a result on any given day? Strip away every habit, ritual, and framework — what's left? You need to know what you're doing (W), you need to concentrate on it without fragmentation (C), and you need to spend the remaining time in a way that sustains you (T). That's it.</p>
        <p>R = (W × C) + T isn't a motivational formula. It's a decomposition. And because W and C are multiplied, not added, the equation reveals something most systems hide: a perfectly clear goal with zero concentration produces zero. Deep focus on the wrong goal also produces zero. You need both. The multiplicative structure is the insight.</p>

        <h3 className="fund-prose-h">Charlie Munger — The three character checks and honest scoring</h3>
        <p>Munger was Warren Buffett's partner for over 60 years and arguably the sharper thinker of the two. His most powerful idea wasn't about investing — it was about thinking itself. Two principles shaped ResultOS directly.</p>
        <p><strong>First: inversion.</strong> Munger said most people ask "how do I succeed?" — the wrong question. The right question is "what would guarantee failure, and how do I avoid that?" We applied this to the equation. Instead of asking "what behaviors lead to high R scores?" we asked "what makes each variable go to zero?" W goes to zero when you pick a decoy goal (self-deception). C goes to zero when attention fragments (no pre-commitment). The feedback loop breaks when you score dishonestly (ego protection). Each of the three character checks — Brutal Honesty, Decisive Protection, Discomfort as Signal — exists to prevent one specific failure mode. They're not aspirational virtues. They're structural safeguards derived from inversion.</p>
        <p><strong>Second: honest feedback loops.</strong> Munger was obsessed with self-deception as the root of bad decisions. The "Did the W ship?" binary in Close the Loop is a Munger principle — it doesn't ask how you feel about the day, it asks whether the outcome happened. Yes or no. No negotiation. The entire scoring system is designed to make self-deception structurally difficult, because Munger understood that a system fed with dishonest data will produce confident wrong answers — worse than no system at all.</p>

        <h3 className="fund-prose-h">Tony Robbins — The T variable and energy management</h3>
        <p>Robbins is often dismissed as a motivational speaker, but his core insight is physiological, not psychological: your state determines your performance, and your state is driven by how you manage your body and energy, not just your mind.</p>
        <p>Most productivity systems treat the human as a cognitive machine — set the right goals, manage your time, execute the plan. Robbins argues that none of this works if your energy is depleted, your body is neglected, and your emotional state is negative. You can have a perfect W and protect your focus blocks, but if you skipped meals, didn't move, and ignored sleep, tomorrow's W × C will collapse.</p>
        <p>The T variable in ResultOS captures this. It's the additive bonus — health, learning, rest, movement, secondary wins. It asks "beyond your primary W, how well did you spend today's time?" A day with a high W × C but a T of 1 means you shipped the work but burned the fuel. That's not sustainable. Robbins would say the T variable is what makes the system survivable across months, not just impressive for a week.</p>

        <h3 className="fund-prose-h">How they blend together</h3>
        <p>The power of ResultOS isn't any single idea — it's the synthesis. Tracy tells you <em>what</em> to focus on. Musk's first principles give you the <em>structure</em> to think about performance. Munger's inversion tells you what to <em>avoid</em> and keeps the system <em>honest</em>. Robbins ensures the system is <em>sustainable</em> by accounting for the human behind the equation.</p>
        <p>Most productivity tools cherry-pick from one school of thought. ResultOS takes the one non-negotiable principle from each thinker — the principle they'd each say matters more than everything else they've ever taught — and wires them together into a single daily operating system. The equation R = (W × C) + T is where these four minds converge.</p>
      </div>

      <div className="fund-hero mb24">
        <div className="fund-kicker">Core Equation</div>
        <div className="fund-equation">
          <span className="R">R</span>
          <span className="op"> = (</span>
          <span className="W">W</span>
          <span className="op"> × </span>
          <span className="C">C</span>
          <span className="op">) + </span>
          <span className="T">T</span>
        </div>
        <div className="fund-subline">Unstoppable people are built one day at a time — when the right result meets deep concentration inside protected time.</div>
        <div className="fund-diagram-row">
          <div className="fund-node">
            <div className="fund-node-badge azure">W</div>
            <div className="fund-node-title">Winning Result</div>
            <div className="fund-node-copy">Clear finish line</div>
          </div>
          <div className="fund-arrow">×</div>
          <div className="fund-node">
            <div className="fund-node-badge sage">C</div>
            <div className="fund-node-title">Concentration</div>
            <div className="fund-node-copy">Focused execution</div>
          </div>
          <div className="fund-arrow">+</div>
          <div className="fund-node">
            <div className="fund-node-badge coral">T</div>
            <div className="fund-node-title">Time Protected</div>
            <div className="fund-node-copy">Distraction removed</div>
          </div>
          <div className="fund-arrow">=</div>
          <div className="fund-node result">
            <div className="fund-node-badge gold">R</div>
            <div className="fund-node-title">Result Score</div>
            <div className="fund-node-copy">Unstoppable result</div>
          </div>
        </div>
      </div>

      <div className="sec">The Unstoppable System</div>
      <div className="g2 mb24">
        <div className="card">
          <div className="cg gold"></div>
          <div className="c-lbl">The Idea</div>
          <div className="fund-card-copy">
            <p>Most people are busy. Few are unstoppable.</p>
            <p>ResultOS is The Unstoppable System — built on one simple idea: your day should not be measured by how much activity you completed. It should be measured by whether you produced the result that mattered.</p>
            <p>Ship your W every day and you don't just become more productive — you become the kind of person who can't be stopped.</p>
          </div>
        </div>
        <div className="card">
          <div className="cg azure"></div>
          <div className="c-lbl">What Makes You Unstoppable</div>
          <div className="fund-quote">Clarity of result × depth of focus × protected time — repeated daily.</div>
          <div className="fund-card-copy">
            Unstoppable isn't a personality trait. It's a daily practice. ResultOS gives that practice a structure: one result, one focus block, one honest review.
          </div>
        </div>
      </div>

      <div className="sec">Where The Variables Mean</div>
      <div className="g3 mb24">
        {variableCards.map((item) => (
          <div key={item.key} className="card fund-card">
            <div className={`cg ${item.tone}`}></div>
            <div className="c-lbl">{item.key} — {item.title}</div>
            <div className={`var-badge ${item.tone}`}>{item.key}</div>
            <div className="fund-card-copy">{item.body}</div>
          </div>
        ))}
      </div>

      <div className="sec">Winning Result</div>
      <div className="g2 mb24">
        <div className="card">
          <div className="cg azure"></div>
          <div className="c-lbl">What W Is</div>
          <div className="fund-card-copy">
            <p><strong>W is the one result that must win today.</strong></p>
            <p>It is not a task list. It is not a vague intention. It is not “I will work on something.” Your W is a clear, measurable, finishable outcome.</p>
            <p>A strong W answers one question:</p>
          </div>
          <div className="fund-quote">“If this one thing is completed today, will the day feel successful?”</div>
        </div>
        <div className="card">
          <div className="cg gold"></div>
          <div className="c-lbl">Rule Of W</div>
          <div className="fund-list">
            <div><strong>Specific</strong> — clear enough to understand</div>
            <div><strong>Measurable</strong> — you can tell if it is done</div>
            <div><strong>Finishable today</strong> — not too big or vague</div>
            <div><strong>Meaningful</strong> — connected to a larger monthly result</div>
          </div>
          <div className="fund-example-callout">
            <div>Bad W: “Grow company.”</div>
            <div>Better W: “Create and publish one AI Agent case study page for getmyforce by 6 PM.”</div>
          </div>
        </div>
      </div>

      <div className="sec">Weak vs Strong W</div>
      <div className="fund-table mb24">
        <div className="fund-table-head">
          <div>Weak W</div>
          <div>Strong W</div>
        </div>
        {weakVsStrong.map(([weak, strong], index) => (
          <div key={index} className="fund-table-row">
            <div>{weak}</div>
            <div>{strong}</div>
          </div>
        ))}
      </div>

      <div className="sec">Monthly Result Area</div>
      <div className="g2 mb24">
        <div className="card">
          <div className="cg gold"></div>
          <div className="c-lbl">Why The Month Comes First</div>
          <div className="fund-card-copy">
            <p>Before you define today’s W, ResultOS asks you to define your <strong>Monthly Result Area</strong>.</p>
            <p>This is the larger outcome your daily work should serve. Without a monthly direction, daily goals become random.</p>
          </div>
          <div className="fund-quote">A strong month gives your daily work direction. If the month is fuzzy, the days drift.</div>
        </div>
        <div className="card">
          <div className="cg sage"></div>
          <div className="c-lbl">Example Monthly Result Areas</div>
          <div className="fund-mini-table">
            {monthlyAreas.map(([area, result]) => (
              <div key={area} className="fund-mini-row">
                <div>{area}</div>
                <div>{result}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="sec">Concentration</div>
      <div className="g2 mb24">
        <div className="card">
          <div className="cg sage"></div>
          <div className="c-lbl">What C Means</div>
          <div className="fund-card-copy">
            <p><strong>C is the quality of your focus while working on your W.</strong></p>
            <p>Many people spend time on important work, but they are mentally scattered. They check messages, switch tabs, respond to random requests, and keep thinking about other tasks.</p>
            <p>That is not concentration.</p>
          </div>
          <div className="fund-quote">One result. One block. One mind.</div>
        </div>
        <div className="card">
          <div className="cg azure"></div>
          <div className="c-lbl">Low C vs High C</div>
          <div className="fund-mini-table">
            {lowHighC.map(([low, high], index) => (
              <div key={index} className="fund-mini-row">
                <div>{low}</div>
                <div>{high}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card mb24">
        <div className="cg sage"></div>
        <div className="c-lbl">Concentration Example</div>
        <div className="fund-example-callout">
          <div>W: “Send 10 personalized outreach messages to UK travel business owners.”</div>
          <div>Low C: You spend 90 minutes, but also check email, WhatsApp, LinkedIn feed, and news. You finish only 3 messages.</div>
          <div>High C: You block 90 minutes, close all unnecessary tabs, use one lead list, and send all 10 messages.</div>
          <div>Same time. Different result. That is why C multiplies W.</div>
        </div>
      </div>

      <div className="sec">Time Protected</div>
      <div className="g2 mb24">
        <div className="card">
          <div className="cg coral"></div>
          <div className="c-lbl">What T Means</div>
          <div className="fund-card-copy">
            <p><strong>T is your ability to protect time from distractions.</strong></p>
            <p>In ResultOS, time is not just clock time. It is protected time. One hour of protected time can produce more than four hours of scattered effort.</p>
            <p>T improves when you keep your phone away, close unnecessary tabs, inform people you are in deep work, avoid meetings during creation time, batch communication, and work in fixed focus blocks.</p>
          </div>
        </div>
        <div className="card">
          <div className="cg gold"></div>
          <div className="c-lbl">Low T vs High T</div>
          <div className="fund-mini-table">
            {lowHighT.map(([low, high], index) => (
              <div key={index} className="fund-mini-row">
                <div>{low}</div>
                <div>{high}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="sec">The ResultOS Equation</div>
      <div className="fund-flow mb24">
        <div className="fund-flow-step">
          <div className="fund-flow-num">1</div>
          <div className="fund-flow-title">Winning Result Is Clear</div>
          <div className="fund-flow-copy">Your mind knows the target.</div>
        </div>
        <div className="fund-flow-step">
          <div className="fund-flow-num">2</div>
          <div className="fund-flow-title">Concentration Is High</div>
          <div className="fund-flow-copy">Your energy gets force.</div>
        </div>
        <div className="fund-flow-step">
          <div className="fund-flow-num">3</div>
          <div className="fund-flow-title">Time Is Protected</div>
          <div className="fund-flow-copy">Your environment stops leaking performance.</div>
        </div>
        <div className="fund-flow-step">
          <div className="fund-flow-num">4</div>
          <div className="fund-flow-title">Result Score Improves</div>
          <div className="fund-flow-copy">Ordinary days become compound days.</div>
        </div>
      </div>

      <div className="g4 mb24">
        {scoreExamples.map((example) => (
          <div key={example.label} className="stat-t">
            <div className="st-l">{example.label}</div>
            <div className="fund-score-values">{example.values}</div>
            <div className="fund-score-result">{example.result}</div>
            <div className="fund-score-note">{example.note}</div>
          </div>
        ))}
      </div>

      <div className="sec">Why W Comes First</div>
      <div className="g2 mb24">
        <div className="card">
          <div className="cg coral"></div>
          <div className="c-lbl">Task Thinking</div>
          <div className="fund-quote soft">“Work on proposal.”</div>
          <div className="fund-card-copy">
            Tasks are inputs. They describe activity, not finish lines.
          </div>
        </div>
        <div className="card">
          <div className="cg gold"></div>
          <div className="c-lbl">Result Thinking</div>
          <div className="fund-quote">“Send final proposal to client by 5 PM.”</div>
          <div className="fund-card-copy">
            Results are outputs. The brain performs better when the finish line is visible.
          </div>
        </div>
      </div>

      <div className="card mb24">
        <div className="cg gold"></div>
        <div className="c-lbl">More Result Thinking Examples</div>
        <div className="fund-example-callout">
          <div>Task: “Study physics.”</div>
          <div>Result: “Complete 30 numericals from Current Electricity and mark all mistakes.”</div>
          <div>Task: “Improve health.”</div>
          <div>Result: “Walk 45 minutes and avoid fried snacks today.”</div>
        </div>
      </div>

      <div className="sec">Morning Ritual</div>
      <div className="g2 mb24">
        <div className="card">
          <div className="cg gold"></div>
          <div className="c-lbl">Purpose</div>
          <div className="fund-quote">Decide what deserves your best energy before the world starts demanding it.</div>
          <div className="fund-card-copy">
            In the morning, ResultOS asks you to define your W, rate clarity, prepare your environment, and commit to a focus block.
          </div>
        </div>
        <div className="card">
          <div className="cg azure"></div>
          <div className="c-lbl">Morning Checklist</div>
          <div className="fund-list">
            {morningChecklist.map((item) => (
              <div key={item}>{item}</div>
            ))}
          </div>
        </div>
      </div>

      <div className="sec">Focus Blocks</div>
      <div className="g2 mb24">
        <div className="card">
          <div className="cg sage"></div>
          <div className="c-lbl">What They Are</div>
          <div className="fund-card-copy">
            <p>Focus Blocks are protected work sessions. You choose a duration: 90, 60, 45, or 25 minutes.</p>
            <p>The goal is not to sit at your desk. The goal is to move your W forward.</p>
          </div>
        </div>
        <div className="card">
          <div className="cg coral"></div>
          <div className="c-lbl">How To Use A Focus Block</div>
          <div className="fund-list">
            {focusBlockSteps.map((item) => (
              <div key={item}>{item}</div>
            ))}
          </div>
        </div>
      </div>

      <div className="sec">End Of Day R Score</div>
      <div className="g2 mb24">
        <div className="card">
          <div className="cg gold"></div>
          <div className="c-lbl">Why Review Matters</div>
          <div className="fund-card-copy">
            <p>At the end of the day, ResultOS asks you to review honestly. Not emotionally. Not optimistically. Honestly.</p>
            <p>You score W, C, and T. The score is not to judge you. It is to diagnose you.</p>
          </div>
        </div>
        <div className="card">
          <div className="cg sage"></div>
          <div className="c-lbl">Example Reflection</div>
          <div className="fund-quote">W was strong, but C collapsed after 3 PM because I kept checking WhatsApp. Tomorrow I will keep notifications off from 10 AM to 1 PM.</div>
          <div className="fund-card-copy">
            This is how performance improves. You do not need motivation. You need diagnosis and correction.
          </div>
        </div>
      </div>

      <div className="sec">Examples By Role</div>
      <div className="fund-role-grid mb24">
        {roleExamples.map((example) => (
          <div key={example.role} className="card">
            <div className="cg gold"></div>
            <div className="c-lbl">{example.role}</div>
            <div className="fund-role-line"><strong>Monthly Result:</strong> {example.monthly}</div>
            <div className="fund-role-line"><strong>Daily W:</strong> {example.daily}</div>
            <div className="fund-role-line"><strong>Focus Block:</strong> {example.block}</div>
            <div className="fund-role-line"><strong>Reflection:</strong> {example.reflection}</div>
          </div>
        ))}
      </div>

      <div className="sec">The Unstoppable Principle</div>
      <div className="card mb24">
        <div className="cg gold"></div>
        <div className="fund-card-copy">
          <p>The Unstoppable System is not about doing more. It is about making the right thing unavoidable.</p>
          <p>When W is clear, your mind knows the target. When C is high, your energy has force. When T is protected, your environment stops leaking performance.</p>
          <p>That is how ordinary days become unstoppable ones — compounding over time into a version of you that cannot be stopped.</p>
        </div>
        <div className="fund-quote">Don't ask, "How busy was I today?" Ask, "Did I ship the result that made me unstoppable?"</div>
      </div>

      <div className="sec">Quick Start Guide</div>
      <div className="fund-flow mb24">
        {quickStart.map(([title, copy], index) => (
          <div key={title} className="fund-flow-step">
            <div className="fund-flow-num">{index + 1}</div>
            <div className="fund-flow-title">{title}</div>
            <div className="fund-flow-copy">{copy}</div>
          </div>
        ))}
      </div>

      <div className="insight">
        <div className="iq">
          The Unstoppable System doesn't ask whether you were busy. It asks whether you shipped the result that mattered — because that's what unstoppable people do, every single day.
        </div>
      </div>
    </div>
  )
}

export default FundamentalsPanel
